/**
 * Lane C0-ROTATE-PORT: which scenery the turned drawing (render/turned/scenery.ts) takes over from the painted props
 * while the camera is turned or tilted, and what it needs to know about each. Kept apart from scenery.ts (which needs
 * the GPU) because terrain.ts, which the ground workers load too, tags the props with it as they are made.
 */
import { Tile, type GameMap, type Structure } from '@nine/shared';
import type { Face3 } from '../buildings';

/** Buildings whose painters note their faces (buildings.ts `faceRec`); boats and terraces stay painted. */
export const TURNED_KINDS: ReadonlySet<string> = new Set(['house', 'pagoda', 'pavilion', 'inn', 'gate', 'moongate']);

/** Stone blocks (rock and wall tiles) are boxes: drawn turned everywhere but on snow (their art is recoloured there). */
export function turnedBlock(m: GameMap, t: number): boolean {
  return (t === Tile.Rock || t === Tile.Wall) && m.biome !== 'snow';
}

/**
 * What a turned prop stands for: a building (every strip of it carries the same object), or a stone block of one tile
 * `h` art pixels tall. Map tiles are those of the view the props were made for (the camera keeps quarter turn 0).
 */
export type TurnedSource =
  | {
      readonly kind: 'building';
      /** The structure as drawn (layoutLook.ts `structureLook`). */
      readonly st: Structure;
      /** Its painted picture and anchor (the footprint's north corner). */
      readonly img: HTMLCanvasElement;
      readonly ax: number;
      readonly ay: number;
      /** Painted along x but standing along y (a gate's mirror image, scenery.ts `alongAxis`). */
      readonly swap: boolean;
    }
  | { readonly kind: 'block'; readonly i: number; readonly j: number; readonly h: number };

/** The faces each painter noted while it painted a building picture, by the picture (raw and in the master look). */
export const paintedFaces = new WeakMap<HTMLCanvasElement, readonly Face3[]>();
